import { defineStore } from 'pinia'
import { ref, computed, reactive } from 'vue'
import { generateDeck, dealCards } from '../utils/letterDistribution.js'
import { calculateWordScore, isValidWordFormat } from '../utils/scoring.js'

// 常见英文单词列表（按长度和常用度排序，用于AI队友出词）
const COMMON_WORDS = [
  // 7+ 字母高分词
  'playing','learned','treated','perfect','quality','thought','brought','without',
  // 6字母词
  'player','played','called','turned','looked','placed','supply','create','please','system',
  // 5字母词
  'about','after','along','before','being','could','doing','early','every','first','going','great','group','happy','large','later','light','might','never','night','other','party','place','point','right','round','short','small','sound','speak','start','state','still','story','their','there','these','thing','think','three','times','today','under','until','using','water','where','which','while','world','would','write','years','young',
  // 4字母词
  'able','also','back','been','call','came','come','dark','date','dead','dear','deep','each','even','ever','face','fact','fall','fast','feel','fine','fire','fish','five','foot','form','from','full','gave','give','goes','gold','good','gray','grew','hair','half','hand','have','head','help','here','high','hold','home','hope','hour','hour','just','kept','kind','knew','know','lake','land','last','lead','left','less','life','like','line','live','long','look','love','made','make','many','mark','mean','mice','milk','mind','more','most','much','must','name','near','neck','need','news','next','nice','once','only','open','over','pace','page','pain','pair','park','part','pass','path','peak','pick','pink','play','plus','port','pull','pure','push','race','rain','read','real','rest','rice','rich','ride','ring','rise','road','rock','room','rule','said','same','sand','save','seal','seat','seek','seem','seen','self','sell','send','sent','ship','shop','shot','show','side','sign','sing','size','skin','slip','slow','snow','soap','soft','soil','sold','sole','some','song','soon','soul','spot','star','stay','stem','step','stop','such','sure','take','tale','talk','tall','team','tear','tell','tend','tent','term','test','text','than','that','them','then','they','thin','this','thus','tick','tide','tied','tier','tile','time','tiny','told','toll','tone','took','tool','top','toss','tour','town','trap','tree','trek','trim','trip','true','tune','turn','twin','type','unit','used','user','vary','vase','very','view','visa','vote','wade','wage','wait','wake','walk','wall','want','ward','warm','warn','wary','wash','wave','ways','weak','wear','week','west','what','when','whom','wide','wife','wild','will','wind','wine','wing','wish','with','woke','wolf','wood','wool','word','wore','work','worm','worn','wrap','yard','year','yell','your',
  // 3字母词
  'ace','act','add','age','ago','aid','aim','air','all','and','any','ape','arc','are','ark','arm','art','ash','ask','ate','awe','axe','aye','bad','bag','ban','bar','bat','bay','bed','bee','bet','bid','big','bin','bit','boa','bog','bow','box','boy','bud','bug','bum','bun','bus','but','buy','cab','can','cap','car','cat','cob','cod','cog','cop','cot','cow','cry','cub','cup','cut','dam','day','den','did','die','dig','dim','dog','dot','dry','dub','dud','due','dug','ear','eat','ebb','eel','egg','ego','elf','elk','elm','emu','end','era','err','eve','eye','fan','far','fat','fax','fed','fee','few','fig','fin','fir','fit','fix','fly','foe','fog','for','fox','fry','fun','fur','gap','gas','gate','gave','gay','get','gnu','god','got','gum','gun','gut','guy','gym','had','hag','ham','has','hat','hay','hem','hen','her','hew','hex','hey','hid','him','hip','his','hit','hog','hop','hot','how','hub','hue','hug','hum','hut','ice','ill','imp','ink','inn','ion','its','jar','jaw','jet','jog','joy','jug','key','kid','kin','kit','lab','lad','lag','lap','law','lay','lea','led','leg','let','lid','lie','lip','lit','log','lot','low','lug','map','man','mat','may','men','met','mid','mix','mob','mom','mop','mud','mug','nap','net','new','nod','nor','not','now','nub','nun','nut','oak','oat','odd','off','oft','oil','old','one','opt','ore','our','out','owe','own','pad','pal','pan','par','pat','paw','pay','pea','peg','pen','per','pet','pew','pie','pig','pin','pit','pot','pox','pro','pry','pub','pun','pup','put','rag','ram','ran','rap','rat','raw','ray','red','ref','rep','rid','rig','rim','rob','rod','roe','rot','row','rub','rug','run','rut','rye','sac','sad','sag','sat','saw','say','sea','see','set','sew','sex','shy','sin','sip','sir','sis','sit','six','ski','sky','sly','sob','sod','son','sop','sow','sox','soy','spa','spy','stab','stag','star','stay','stem','step','stew','stir','stop','stub','stud','sum','sun','tab','tad','tag','tan','tap','tar','tat','tax','tea','tee','ten','the','thy','tic','tie','tin','tip','toe','ton','too','top','tot','toy','try','tub','tug','two','ugh','van','vat','vet','vex','via','vie','wad','wag','war','was','wax','way','web','wed','wee','wet','who','why','wig','win','wit','won','yes','yet','yew','you','zap','zen','zip','zoo'
]

export const useGameStore = defineStore('game', () => {
// 模式设置
const HAND_SIZE_BY_MODE = { classic: 10, lightning: 7, coop: 9 }
const COOP_POOL_SIZE = 6
const COOP_TARGET = 150
const COOP_ROUNDS = 12
const CLASSIC_ROUNDS = 12

// 状态
const gameState = ref('lobby') // lobby, waiting, playing, ended
const gameMode = ref('classic')
const players = ref(['localPlayer','teammate'])
const currentPlayer = ref('localPlayer')
const round = ref(1)
const deck = ref([])

// 手牌与选择
const playerHand = ref([])
const selectedCards = ref([])
const teammateHand = ref([])

// 协作模式：公共池
const publicPool = ref([])

// 数据
const MODE_KEYS = ['classic', 'lightning', 'coop']
const defaultScores = () => ({ localPlayer: 0, teammate: 0 })
const scoresByMode = reactive({
  classic: defaultScores(),
  lightning: defaultScores(),
  coop: defaultScores()
})
const logsByMode = {
  classic: ref([]),
  lightning: ref([]),
  coop: ref([])
}
const ensureModeKey = (mode) => (MODE_KEYS.includes(mode) ? mode : 'classic')
const activeModeKey = computed(() => ensureModeKey(gameMode.value))
const scores = computed(() => scoresByMode[activeModeKey.value])
const gameLog = computed(() => logsByMode[activeModeKey.value].value)
const addLogEntry = (entry) => {
  logsByMode[activeModeKey.value].value.push({
    timestamp: Date.now(),
    ...entry
  })
}
const setScores = (player, value) => {
  scores.value[player] = value
}
const increaseScore = (player, delta) => {
  const current = scores.value[player] || 0
  scores.value[player] = current + delta
}
const timer = ref(null)
const turnDuration = ref(0)
const turnRemaining = ref(0)
const isResolving = ref(false)

// 计算属性
const handSize = computed(() => HAND_SIZE_BY_MODE[gameMode.value] || 10)
const isCoop = computed(() => gameMode.value === 'coop')
const teamScore = computed(() => (scores.value['localPlayer'] || 0) + (scores.value['teammate'] || 0))
const classicScore = computed(() => (scores.value['localPlayer'] || 0))
const isPlayerTurn = computed(() => currentPlayer.value === 'localPlayer')
const canPlay = computed(() => {
const letterCount = selectedCards.value.filter(c => c && c.letter).length
return isPlayerTurn.value && letterCount >= 2 && !isResolving.value
})
const gameHistory = computed(() => gameLog.value.filter(e => e.type === 'word_played'))

// 回合计时
const startTurnTimer = () => {
stopTurnTimer()
if (turnDuration.value <= 0) return
turnRemaining.value = turnDuration.value
timer.value = setInterval(() => {
turnRemaining.value -= 1
if (turnRemaining.value <= 0) {
stopTurnTimer()
passTurn('timeout')
}
}, 1000)
}
const stopTurnTimer = () => {
if (timer.value) {
clearInterval(timer.value)
timer.value = null
}
}
const resetTurnTimerForMode = () => {
if (gameMode.value === 'lightning') turnDuration.value = 30
else if (gameMode.value === 'coop') turnDuration.value = 45
else turnDuration.value = 0
startTurnTimer()
}

// 手牌多样性：限制重复 & 保证元音
const VOWELS = new Set(['A', 'E', 'I', 'O', 'U'])
const isLetterCard = (card) => card && card.type === 'letter' && card.letter
const isVowel = (card) => isLetterCard(card) && VOWELS.has(card.letter)

const replaceCardFromDeck = (hand, deckArr, handIdx, predicate) => {
  if (handIdx < 0 || handIdx >= hand.length) return false
  const deckIdx = deckArr.findIndex((card) => isLetterCard(card) && (!predicate || predicate(card)))
  if (deckIdx === -1) return false
  const replacement = deckArr.splice(deckIdx, 1)[0]
  const [removed] = hand.splice(handIdx, 1, replacement)
  if (removed) deckArr.push(removed)
  return true
}

const enforceHandDiversity = (hand, deckArr, { maxDuplicates = 3, minVowels = 3, soft = false } = {}) => {
  if (!Array.isArray(hand) || !Array.isArray(deckArr) || !hand.length) return

  const counts = {}
  hand.forEach((card) => {
    if (!isLetterCard(card)) return
    counts[card.letter] = (counts[card.letter] || 0) + 1
  })

  const overLetters = Object.keys(counts).filter((letter) => counts[letter] > maxDuplicates)
  let attempts = 0
  for (const letter of overLetters) {
    while (counts[letter] > maxDuplicates) {
      const idx = hand.findIndex((c) => isLetterCard(c) && c.letter === letter)
      if (idx === -1) break
      const swapped = replaceCardFromDeck(hand, deckArr, idx, (deckCard) => deckCard.letter !== letter)
      if (!swapped) break
      counts[letter] -= 1
      const newLetter = hand[idx]?.letter
      if (newLetter) counts[newLetter] = (counts[newLetter] || 0) + 1
      attempts++
      if (soft && attempts > 20) break
    }
    if (soft && attempts > 20) break
  }

  const vowelCount = () => hand.reduce((sum, card) => sum + (isVowel(card) ? 1 : 0), 0)
  let currentVowels = vowelCount()
  attempts = 0
  while (currentVowels < minVowels) {
    const deckIdx = deckArr.findIndex((card) => isLetterCard(card) && isVowel(card))
    if (deckIdx === -1) break
    const handIdx = hand.findIndex((card) => isLetterCard(card) && !isVowel(card))
    if (handIdx === -1) break
    replaceCardFromDeck(hand, deckArr, handIdx, (card) => isVowel(card))
    currentVowels++
    attempts++
    if (soft && attempts > 20) break
  }
}

// 仅在“回合开始”自动填满 Coop 公共池
const ensurePoolAtTurnStart = () => {
if (!isCoop.value) return
const target = COOP_POOL_SIZE
while (publicPool.value.length < target && deck.value.length > 0) {
const idx = deck.value.findIndex(c => c && c.type === 'letter')
if (idx === -1) break
const c = deck.value.splice(idx, 1)[0]
publicPool.value.push({ ...c, _from: 'deck' })
}
}

// 动作：创建/加入（保留空实现，可接后端）
const createGame = async (mode, playerCount) => {
gameState.value = 'waiting'
gameMode.value = mode
}
const joinGame = async (gameId) => {
gameState.value = 'waiting'
}

// 开始游戏（单人演示）
const startGame = () => {
gameState.value = 'playing'
round.value = 1
setScores('localPlayer', 0)
setScores('teammate', 0)
logsByMode[activeModeKey.value].value = []
players.value = ['localPlayer','teammate']
currentPlayer.value = 'localPlayer'

    deck.value = generateDeck()
    const dealt = dealCards(deck.value, 2, handSize.value)
    playerHand.value = dealt.hands[0]
    teammateHand.value = dealt.hands[1]
    enforceHandDiversity(playerHand.value, deck.value, { maxDuplicates: 3, minVowels: 3 })
    enforceHandDiversity(teammateHand.value, deck.value, { maxDuplicates: 3, minVowels: 3 })
    deck.value = dealt.remainingDeck

    publicPool.value = []
    if (isCoop.value) {
      ensurePoolAtTurnStart() // 仅开局填满
    }
    resetTurnTimerForMode()
    gameState.value = 'playing'

}

// 选择/取消选择
const selectCard = (card) => {
if (!card || !card.id) return
// 防止重复选择同一张卡片（基于ID）
const existing = selectedCards.value.find(c => c && c.id === card.id)
if (existing) {
  // 如果已选，则取消选择
  selectedCards.value = selectedCards.value.filter(c => c && c.id !== card.id)
} else {
  // 如果未选，则添加
  selectedCards.value.push(card)
}
}

const removePlayedCards = () => {
selectedCards.value.forEach(card => {
const index = playerHand.value.findIndex(c => c.id === card.id)
if (index !== -1) playerHand.value.splice(index, 1)
const poolIndex = publicPool.value.findIndex(c => c.id === card.id)
if (poolIndex !== -1) publicPool.value.splice(poolIndex, 1)
})
selectedCards.value = []
}

const drawCards = (count) => {
for (let i = 0; i < count && deck.value.length > 0; i++) {
const card = deck.value.shift()
    playerHand.value.push(card)
  }
  enforceHandDiversity(playerHand.value, deck.value, { maxDuplicates: 3, minVowels: 3, soft: true })
}

// 批量捐献（单张或多张都支持）；回合中不触发系统自动补
const donateToPool = () => {
if (!isCoop.value) return
const handIds = new Set(playerHand.value.map(c => c.id))
const toDonate = selectedCards.value.filter(c => c && c.type === 'letter' && handIds.has(c.id))
if (!toDonate.length) return
const donateIds = new Set(toDonate.map(c => c.id))
playerHand.value = playerHand.value.filter(c => !donateIds.has(c.id))
publicPool.value.push(...toDonate.map(c => ({ ...c, _from: 'hand' })))
selectedCards.value = selectedCards.value.filter(c => !donateIds.has(c.id))
// 系统不自动补；公共池变化仅由玩家捐献或回合开始时填充
    try {
      const letters = toDonate.map(c => c.letter).filter(Boolean)
      addLogEntry({ type: 'donate', player: 'localPlayer', cards: letters })
    } catch(_) {}
}


  // 队友出词：尝试用公共池+队友手牌拼常见词
  const playTeammateTurn = () => {
    const poolLetters = publicPool.value.filter(c=>c && c.type==='letter').map(c=>c.letter)
    const mateLetters = teammateHand.value.filter(c=>c && c.type==='letter').map(c=>c.letter)

    const letterScore = (ch) => ({A:1,B:3,C:3,D:2,E:1,F:4,G:2,H:4,I:1,J:8,K:5,L:1,M:3,N:1,O:1,P:3,Q:10,R:1,S:1,T:1,U:1,V:4,W:4,X:8,Y:4,Z:10}[ch] || 1)
    const wordScore = (w) => w.split('').reduce((s,c)=>s+letterScore(c),0)
    const CAND = typeof COMMON_WORDS !== 'undefined' ? COMMON_WORDS : []
    const sorted = [...CAND].sort((a,b)=> (b.length-a.length) || (wordScore(b)-wordScore(a)))

    const tryBuild = (word, pool, mate) => {
      const need = word.split('')
      const bp = [...pool], bm = [...mate]
      const usePool = [], useMate = []
      for (const ch of need) {
        let i = bp.indexOf(ch)
        if (i !== -1) { bp.splice(i,1); usePool.push(ch); continue }
        i = bm.indexOf(ch)
        if (i !== -1) { bm.splice(i,1); useMate.push(ch); continue }
        return null
      }
      return { usePool, useMate }
    }

    let chosen = null, usage = null
    for (const w of sorted) {
      const u = tryBuild(w.toUpperCase(), poolLetters, mateLetters)
      if (u) { chosen = w; usage = u; break }
    }
    if (!chosen) { addLogEntry({ type:'no_play', player:'teammate' }); return }
    const lower = chosen.toLowerCase()

    for (const ch of usage.usePool) {
      const i = publicPool.value.findIndex(c=>c && c.type==='letter' && c.letter===ch)
      if (i!==-1) publicPool.value.splice(i,1)
    }
    for (const ch of usage.useMate) {
      const i = teammateHand.value.findIndex(c=>c && c.type==='letter' && c.letter===ch)
      if (i!==-1) teammateHand.value.splice(i,1)
    }
    const s = calculateScore(lower)
    increaseScore('teammate', s)
    addLogEntry({ type:'word_played', player:'teammate', word: lower, score: s, playerRole: 'teammate' })
  }

const restart = () => {
stopTurnTimer()
gameState.value = 'lobby'
selectedCards.value = []
playerHand.value = []
deck.value = []
startGame()
}

const clearSelection = () => {
selectedCards.value = []
}

const passTurn = (kind = 'manual') => {
clearSelection()
nextTurn()
}

const nextTurn = () => {
const maxRounds = gameMode.value === 'classic' ? CLASSIC_ROUNDS : (isCoop.value ? COOP_ROUNDS : Infinity)
const classicEnd = gameMode.value === 'classic' && (round.value >= maxRounds || playerHand.value.length === 0)
const coopEnd = isCoop.value && (teamScore.value >= COOP_TARGET || round.value >= maxRounds)

    if (classicEnd) {
      addLogEntry({
        type: 'game_result', mode: 'classic', success: true,
        score: classicScore.value, round: round.value,
        reason: playerHand.value.length === 0 ? 'empty_hand' : 'round_limit'
      })
      gameState.value = 'ended'; stopTurnTimer(); return
    }
    if (coopEnd) {
      addLogEntry({
        type: 'game_result', mode: 'coop', success: teamScore.value >= COOP_TARGET,
        score: teamScore.value, round: round.value
      })
      gameState.value = 'ended'; stopTurnTimer(); return
    }

    // Coop：交替回合（local -> teammate -> local）
    if (isCoop.value) {
      if (currentPlayer.value === 'localPlayer') {
        currentPlayer.value = 'teammate'
        // 执行队友回合
        playTeammateTurn()
      } else {
        currentPlayer.value = 'localPlayer'
        round.value += 1
        // 新回合开始补满公共池
        ensurePoolAtTurnStart()
      }
    } else {
      round.value += 1
    }
    resetTurnTimerForMode()

}

// 校验/计分
// 校验目标词能否由“已选卡牌”或“自动从手牌/公共池匹配”得到（选项B：宽松）
const validateWord = async (word) => {
  if (!isValidWordFormat(word)) return false
  const needed = Array.from(word.toUpperCase())
  // 先看是否用已选卡即可满足
  const chosen = selectedCards.value.filter(c => c && c.type === 'letter').map(c => c.letter)
  const bagChosen = [...chosen]
  let okChosen = true
  for (const ch of needed) {
    const i = bagChosen.indexOf(ch)
    if (i === -1) { okChosen = false; break }
    bagChosen.splice(i, 1)
  }
  if (okChosen) return true
  // 自动从手牌+公共池匹配
  const handLetters = playerHand.value.filter(c=>c && c.type==='letter').map(c=>c.letter)
  const poolLetters = publicPool.value.filter(c=>c && c.type==='letter').map(c=>c.letter)
  const bagAuto = [...handLetters, ...poolLetters]
  for (const ch of needed) {
    const i = bagAuto.indexOf(ch)
    if (i === -1) return false
    bagAuto.splice(i, 1)
  }
  return true
}
const calculateScore = (word, lettersUsed) => {
  const usedLetters = Array.isArray(lettersUsed)
    ? lettersUsed
    : selectedCards.value.filter(c => c.type === 'letter').map(c => c.letter)
  return calculateWordScore(word, usedLetters)
}

// 出词（供组件调用）
const playWord = async (word) => {
  if (!isPlayerTurn.value || isResolving.value) return false
  const w = String(word || '').trim()
  if (!isValidWordFormat(w)) return false
  isResolving.value = true
  try {
    const ok = await validateWord(w)
    if (!ok) {
      isResolving.value = false
      return false
    }
    // 确定要消耗的字母：先用已选，不足再自动从手牌/公共池补足
    const needed = Array.from(w.toUpperCase())
    const consumeFromSelected = []
    const consumeFromHand = []
    const consumeFromPool = []

    // 尝试用已选卡匹配
    const selCopy = selectedCards.value.filter(c=>c && c.type==='letter').map(c=>c.letter)
    for (const ch of needed) {
      const i = selCopy.indexOf(ch)
      if (i !== -1) { selCopy.splice(i,1); consumeFromSelected.push(ch) }
    }
    // 计算剩余需求
    const remaining = []
    {
      const used = [...consumeFromSelected]
      for (const ch of needed) {
        const i = used.indexOf(ch)
        if (i !== -1) { used.splice(i,1); continue }
        remaining.push(ch)
      }
    }
    // 从手牌/公共池补足
    for (const ch of remaining) {
      let i = playerHand.value.findIndex(c=>c && c.type==='letter' && c.letter===ch)
      if (i !== -1) { consumeFromHand.push(ch); playerHand.value.splice(i,1); continue }
      i = publicPool.value.findIndex(c=>c && c.type==='letter' && c.letter===ch)
      if (i !== -1) { consumeFromPool.push(ch); publicPool.value.splice(i,1); continue }
    }

    const allUsed = [...consumeFromSelected, ...consumeFromHand, ...consumeFromPool]
    const score = calculateScore(w, allUsed)
    increaseScore('localPlayer', score)
    addLogEntry({ type:'word_played', player:'localPlayer', word: w.toLowerCase(), score, playerRole: 'local' })

    // 从 selectedCards 移除已使用的（只移除符合的数量）
    const removeOneFromSelected = (ch) => {
      const idx = selectedCards.value.findIndex(c=>c && c.type==='letter' && c.letter===ch)
      if (idx !== -1) selectedCards.value.splice(idx,1)
    }
    for (const ch of consumeFromSelected) removeOneFromSelected(ch)

    // 补牌至满手
    drawCards(handSize.value - playerHand.value.length)

    // 先完成玩家出词的状态更新，立即释放锁，让玩家可以继续操作
    isResolving.value = false

    // 队友回合在后台异步执行，不阻塞玩家操作
    // 使用 nextTick 确保 isResolving 状态更新已生效
    await new Promise(resolve => setTimeout(resolve, 0))
    nextTurn()
    return true
  } catch (e) {
    isResolving.value = false
    throw e
  }
}

return {
// 常量
COOP_POOL_SIZE, COOP_TARGET, COOP_ROUNDS, CLASSIC_ROUNDS,
// 状态
gameState, gameMode, players, currentPlayer, round, deck,
playerHand, teammateHand, selectedCards, publicPool, scores, gameLog, timer,
turnDuration, turnRemaining, isResolving, handSize,
// 计算属性
isPlayerTurn, canPlay, gameHistory, isCoop, teamScore, classicScore,
// 动作
createGame, joinGame, startGame, playWord, selectCard,
restart, passTurn, donateToPool, nextTurn, clearSelection, playTeammateTurn,
ensurePoolAtTurnStart
}
})
